import { isNonProduction } from "@/utils/common";
import { getCORSSafeURL } from "@/utils/url";
import type { MarketingResource } from "creso-core/types";
import { webDownload } from "creso-core/hooks";
import html2canvas from "html2canvas-pro";
import JSZip from "jszip";
import { createRoot } from "react-dom/client";
import { sleep } from "radash";
import { getWebStoryImageAttachments } from "./generate-web-story-video";
import { WebStoryBrandingDetails } from "../components/web-story-end-card";

type WebStoryDownloadSlideProps = {
  src: string;
  name: string;
  arn: string;
  width: number;
  height: number;
};

function WebStoryDownloadSlide({
  src,
  name,
  arn,
  width,
  height,
}: WebStoryDownloadSlideProps) {
  return (
    <div
      className="relative overflow-hidden bg-black"
      style={{ width, height }}
    >
      <img
        src={getCORSSafeURL(src)}
        alt=""
        crossOrigin="anonymous"
        className="size-full object-contain"
      />
      <div className="absolute inset-x-0 bottom-0 flex flex-row justify-between bg-[#1E293B] px-10 py-6">
        <div className="text-4xl text-white">{name}</div>
        <div className="text-4xl text-white">{arn}</div>
      </div>
    </div>
  );
}

function loadImageDimensions(
  imageUrl: string
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      resolve({
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
    };
    image.onerror = () => reject(new Error("Failed to load image"));
    image.src = getCORSSafeURL(imageUrl);
  });
}

async function captureWebStoryDownloadSlide(
  imageUrl: string,
  branding: WebStoryBrandingDetails
): Promise<Blob> {
  const { width, height } = await loadImageDimensions(imageUrl);

  const host = document.createElement("div");
  host.style.position = "fixed";
  host.style.left = "-9999px";
  host.style.top = "0";
  host.style.pointerEvents = "none";
  document.body.appendChild(host);

  const root = createRoot(host);

  try {
    root.render(
      <WebStoryDownloadSlide
        src={imageUrl}
        name={branding.distributorName}
        arn={branding.arn}
        width={width}
        height={height}
      />
    );

    await sleep(800);

    const element = host.firstElementChild as HTMLElement | null;
    if (!element) {
      throw new Error("Failed to render download slide");
    }

    const canvas = await html2canvas(element, {
      width,
      height,
      scale: 1,
      useCORS: true,
      allowTaint: true,
      logging: isNonProduction(),
      backgroundColor: "#000000",
      onclone: async () => {
        await sleep(500);
      },
    });

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Failed to export image"));
            return;
          }
          resolve(blob);
        },
        "image/png",
        1
      );
    });
  } finally {
    root.unmount();
    document.body.removeChild(host);
  }
}

function sanitizeFileName(name: string) {
  return name
    .replace(/[^\w.-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

function getImageExtension(url: string, blobType: string) {
  const urlMatch = url.match(/\.(jpe?g|png|webp|gif)(\?|$)/i);
  if (urlMatch) {
    const ext = urlMatch[1].toLowerCase();
    return ext === "jpeg" ? ".jpg" : `.${ext}`;
  }

  if (blobType === "image/jpeg") return ".jpg";
  if (blobType === "image/webp") return ".webp";
  if (blobType === "image/gif") return ".gif";

  return ".png";
}

export async function downloadWebStoryImagesAsZip(
  item: MarketingResource,
  branding?: WebStoryBrandingDetails
) {
  const attachments = getWebStoryImageAttachments(item);

  if (attachments.length === 0) {
    throw new Error("No images available to download");
  }

  const zip = new JSZip();
  const baseName = sanitizeFileName(item.title) || "web-story";

  await Promise.all(
    attachments.map(async (attachment, index) => {
      let blob: Blob;
      let extension: string;

      if (branding) {
        blob = await captureWebStoryDownloadSlide(
          attachment.attachment,
          branding
        );
        extension = ".png";
      } else {
        const response = await fetch(getCORSSafeURL(attachment.attachment));

        if (!response.ok) {
          throw new Error("Failed to download image");
        }

        blob = await response.blob();
        extension = getImageExtension(attachment.attachment, blob.type);
      }

      const entryName = `${baseName}-${index + 1}${extension}`;
      zip.file(entryName, blob);
    })
  );

  const zipBlob = await zip.generateAsync({ type: "blob" });
  webDownload(zipBlob, `${baseName}.zip`);
}
